/**
 * Cash Register Modal
 * Handles opening and closing the cash register (caixa).
 * Opening: input initial cash amount.
 * Closing: shows shift summary, input counted amount, calculates difference.
 */
import { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, DollarSign, TrendingUp, TrendingDown, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/api';

interface CashRegisterModalProps {
  isOpen: boolean;
  mode: 'open' | 'close';
  onClose: () => void;
  onSuccess: () => void;
}

interface ShiftSummary {
  register_id: string;
  opened_at: string;
  initial_amount: number;
  sales_by_method: Array<{ method: string; total: number; count: number }>;
  total_sangrias: number;
  total_suprimentos: number;
  total_sales: number;
}

const METHOD_LABELS: Record<string, string> = {
  dinheiro: 'Dinheiro',
  cartao_debito: 'Cartão Débito',
  cartao_credito: 'Cartão Crédito',
  pix: 'PIX',
  fiado: 'Credito',
};

export function CashRegisterModal({
  isOpen,
  mode,
  onClose,
  onSuccess,
}: CashRegisterModalProps) {
  const [initialAmount, setInitialAmount] = useState('');
  const [countedAmount, setCountedAmount] = useState('');
  const [summary, setSummary] = useState<ShiftSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);

  // Load summary when closing
  useEffect(() => {
    if (isOpen && mode === 'close') {
      setLoadingSummary(true);
      apiFetch('/api/cash-register/summary')
        .then((res) => res.json())
        .then((data) => {
          if (data.error) {
            toast.error(data.error);
          } else {
            setSummary(data);
          }
        })
        .catch(() => toast.error('Erro ao carregar resumo'))
        .finally(() => setLoadingSummary(false));
    }
  }, [isOpen, mode]);

  const resetForm = useCallback(() => {
    setInitialAmount('');
    setCountedAmount('');
    setSummary(null);
    setLoading(false);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleOpen = useCallback(async () => {
    const amount = parseFloat(initialAmount);
    if (isNaN(amount) || amount < 0) {
      toast.error('Informe um valor inicial válido');
      return;
    }

    setLoading(true);
    try {
      const response = await apiFetch('/api/cash-register/open', {
        method: 'POST',
        body: JSON.stringify({ initial_amount: amount }),
      });

      const data = await response.json();

      if (!response.ok) {
        toast.error(data.error || 'Erro ao abrir caixa');
        setLoading(false);
        return;
      }

      toast.success(`Caixa aberto com R$${amount.toFixed(2)}`);
      onSuccess();
      handleClose();
    } catch {
      toast.error('Erro de conexão');
      setLoading(false);
    }
  }, [initialAmount, onSuccess, handleClose]);

  const handleCloseRegister = useCallback(async () => {
    const amount = parseFloat(countedAmount);
    if (isNaN(amount) || amount < 0) {
      toast.error('Informe o valor contado');
      return;
    }

    setLoading(true);
    try {
      const response = await apiFetch('/api/cash-register/close', {
        method: 'POST',
        body: JSON.stringify({ counted_amount: amount }),
      });

      const data = await response.json();

      if (!response.ok) {
        toast.error(data.error || 'Erro ao fechar caixa');
        setLoading(false);
        return;
      }

      const diffStr = data.difference >= 0
        ? `+R$${data.difference.toFixed(2)}`
        : `-R$${Math.abs(data.difference).toFixed(2)}`;

      toast.success(`Caixa fechado. Diferença: ${diffStr}`);
      onSuccess();
      handleClose();
    } catch {
      toast.error('Erro de conexão');
      setLoading(false);
    }
  }, [countedAmount, onSuccess, handleClose]);

  const formatCurrency = (v: number) =>
    v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  // Calculate expected for preview
  const expectedAmount = summary
    ? summary.initial_amount +
      (summary.sales_by_method.find((s) => s.method === 'dinheiro')?.total || 0) -
      summary.total_sangrias +
      summary.total_suprimentos
    : 0;

  const countedNum = parseFloat(countedAmount) || 0;
  const difference = countedNum - expectedAmount;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <motion.div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          />
          <motion.div
            className="relative z-10 w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-xl"
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
          >
            <button
              onClick={handleClose}
              className="absolute right-4 top-4 rounded-sm p-1 text-muted-foreground hover:text-foreground"
              aria-label="Fechar"
            >
              <X className="h-4 w-4" />
            </button>

            {/* OPEN MODE */}
            {mode === 'open' && (
              <>
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-500/10">
                    <DollarSign className="h-5 w-5 text-green-500" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold">Abrir Caixa</h2>
                    <p className="text-sm text-muted-foreground">
                      Informe o valor inicial em dinheiro
                    </p>
                  </div>
                </div>

                <div className="mt-5 space-y-4">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-foreground">
                      Valor Inicial (R$)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={initialAmount}
                      onChange={(e) => setInitialAmount(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleOpen()}
                      placeholder="0.00"
                      className="w-full rounded-lg border border-border bg-background px-3 py-3 text-lg font-medium text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                      autoFocus
                    />
                  </div>

                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Clock className="h-3.5 w-3.5" />
                    <span>{new Date().toLocaleString('pt-BR')}</span>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={handleClose}
                      className="flex-1 rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleOpen}
                      disabled={loading}
                      className="flex-1 rounded-lg bg-green-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-green-700 disabled:opacity-50"
                    >
                      {loading ? 'Abrindo...' : 'Abrir Caixa'}
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* CLOSE MODE */}
            {mode === 'close' && (
              <>
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-500/10">
                    <DollarSign className="h-5 w-5 text-orange-500" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold">Fechar Caixa</h2>
                    <p className="text-sm text-muted-foreground">Resumo do turno</p>
                  </div>
                </div>

                {loadingSummary ? (
                  <div className="mt-5 flex items-center justify-center py-8">
                    <span className="text-sm text-muted-foreground">Carregando resumo...</span>
                  </div>
                ) : summary ? (
                  <div className="mt-5 space-y-4">
                    {/* Summary */}
                    <div className="space-y-2 rounded-lg border border-border/50 bg-muted/30 p-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Valor inicial:</span>
                        <span>{formatCurrency(summary.initial_amount)}</span>
                      </div>
                      {summary.sales_by_method.map((s) => (
                        <div key={s.method} className="flex justify-between text-sm">
                          <span className="text-muted-foreground">
                            {METHOD_LABELS[s.method] || s.method} ({s.count}x):
                          </span>
                          <span className="text-green-500">+{formatCurrency(s.total)}</span>
                        </div>
                      ))}
                      {summary.total_sangrias > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <TrendingDown className="h-3 w-3 text-red-400" />
                            Sangrias:
                          </span>
                          <span className="text-red-400">-{formatCurrency(summary.total_sangrias)}</span>
                        </div>
                      )}
                      {summary.total_suprimentos > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <TrendingUp className="h-3 w-3 text-green-400" />
                            Suprimentos:
                          </span>
                          <span className="text-green-400">+{formatCurrency(summary.total_suprimentos)}</span>
                        </div>
                      )}
                      <div className="border-t border-border/50 pt-1">
                        <div className="flex justify-between text-sm font-medium">
                          <span>Valor esperado (dinheiro):</span>
                          <span>{formatCurrency(expectedAmount)}</span>
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Total de vendas: {summary.total_sales}
                      </div>
                    </div>

                    {/* Counted Amount */}
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-foreground">
                        Valor Contado (R$)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={countedAmount}
                        onChange={(e) => setCountedAmount(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleCloseRegister()}
                        placeholder="0.00"
                        className="w-full rounded-lg border border-border bg-background px-3 py-3 text-lg font-medium text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                        autoFocus
                      />
                    </div>

                    {/* Difference Preview */}
                    {countedAmount && (
                      <div
                        className={`rounded-lg border p-3 ${
                          difference === 0
                            ? 'border-green-500/30 bg-green-500/5'
                            : difference > 0
                              ? 'border-blue-500/30 bg-blue-500/5'
                              : 'border-red-500/30 bg-red-500/5'
                        }`}
                      >
                        <div className="flex justify-between text-sm font-medium">
                          <span>Diferença:</span>
                          <span
                            className={
                              difference === 0
                                ? 'text-green-500'
                                : difference > 0
                                  ? 'text-blue-500'
                                  : 'text-red-500'
                            }
                          >
                            {difference >= 0 ? '+' : ''}
                            {formatCurrency(difference)}
                          </span>
                        </div>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {difference === 0
                            ? 'Caixa confere!'
                            : difference > 0
                              ? 'Sobra no caixa'
                              : 'Falta no caixa'}
                        </p>
                      </div>
                    )}

                    <div className="flex gap-3 pt-2">
                      <button
                        onClick={handleClose}
                        className="flex-1 rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={handleCloseRegister}
                        disabled={loading || !countedAmount}
                        className="flex-1 rounded-lg bg-orange-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-orange-700 disabled:opacity-50"
                      >
                        {loading ? 'Fechando...' : 'Fechar Caixa'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-5 py-8 text-center text-sm text-muted-foreground">
                    Nenhum caixa aberto encontrado.
                  </div>
                )}
              </>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
